import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router'
import { ArrowLeft, Banknote, FileDown, FilePen, Plus, Trash2 } from 'lucide-react'
import {
  CORRECTION_STATUSES,
  ID_DOCUMENT_TYPES,
  SYSTEM_TYPES,
  FILE_TYPES,
  allFields,
  type Answers,
  type ApplicationState,
  type CorrectionStatus,
  type DocumentInfo,
  type IdDocumentType,
  type IdScanInfo,
  type FormField,
  type FormSchema,
  type PaymentState,
  type SummarySection,
} from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  InfoGrid,
  InfoItem,
  Input,
  MatriculeTag,
  Modal,
  PageHeader,
  SegmentedPills,
  Select,
  StatusBadge,
  Tabs,
  Textarea,
  useAuth,
  useToast,
  type BadgeVariant,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import { APPLICATIONS_KEY } from './ApplicationsPage'
import { DocumentsCard } from './DocumentsCard'
import { IdentityScansCard } from './IdentityScansCard'
import {
  ComplementsCard,
  DecisionsCard,
  InstructionCard,
  SystemChecksCard,
  type ReviewState,
} from './ReviewPanel'
import { PaymentHistory } from './PaymentHistory'
import { saveFile } from '../../lib/documents'

export interface Correction {
  id: string
  status: CorrectionStatus
  changes: {
    key: string
    label: string
    before: unknown
    after: unknown
    beforeDisplay: string | null
    afterDisplay: string | null
  }[]
  reason: string
  requestedBy: { id: string; label: string | null }
  requestedAt: string
  decidedBy: { id: string; label: string | null } | null
  decidedAt: string | null
  decisionNote: string | null
}

interface Detail {
  id: string
  applicationNumber: string
  status: ApplicationState
  paymentStatus: PaymentState
  category: string | null
  submittedAt: string | null
  submissionRef: string | null
  receiptAvailable: boolean
  competition: { id: string; reference: string; title: string; session: string }
  candidate: {
    id: string
    userNumber: string | null
    lastName: string | null
    firstName: string | null
    email: string
    phone: string | null
    idDocumentType: IdDocumentType | null
  }
  form: { versionId: string; version: number; schema: FormSchema }
  answers: Answers
  summary: SummarySection[]
  history: { to: ApplicationState; toLabel: string; reason: string | null; at: string }[]
  snapshots: {
    version: number
    kind: 'submission' | 'correction'
    sha256: string
    createdAt: string
  }[]
  corrections: Correction[]
  documents: DocumentInfo[]
  identityScans: IdScanInfo[]
  assignee: ReviewState['assignee']
  reviewStartedAt: string | null
  decidedAt: string | null
  systemChecks: ReviewState['systemChecks']
  blockers: ReviewState['blockers']
  decisions: ReviewState['decisions']
  complements: ReviewState['complements']
  can: { requestCorrection: boolean; reviewDocuments: boolean } & ReviewState['can']
}

type Tab = 'infos' | 'reponses' | 'pieces' | 'paiement' | 'historique' | 'decisions'

export const CORRECTION_VARIANT: Record<CorrectionStatus, BadgeVariant> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'neutral',
}

const show = (v: string | null) => v ?? '—'

export function ApplicationPage() {
  const { id = '' } = useParams()
  const { session } = useAuth()
  const toast = useToast()
  const [version, setVersion] = useState<number | null>(null)
  const [correcting, setCorrecting] = useState(false)
  const [paying, setPaying] = useState(false)
  const [tab, setTab] = useState<Tab>('infos')
  const detail = useQuery({
    queryKey: [...APPLICATIONS_KEY, id],
    queryFn: () => session.api.get<Detail>(`/admin/applications/${id}`),
  })
  const latest = detail.data?.snapshots.at(-1)?.version ?? null
  const shown = version ?? latest
  const snapshot = useQuery({
    queryKey: [...APPLICATIONS_KEY, id, 'snapshot', shown],
    queryFn: () =>
      session.api.get<{ content: { summary: SummarySection[] } }>(
        `/admin/applications/${id}/snapshots/${shown}`
      ),
    enabled: Boolean(shown && shown !== latest),
  })
  const download = useMutation({
    mutationFn: () => session.api.download(`/admin/applications/${id}/receipt`),
    onSuccess: (file) => {
      const url = URL.createObjectURL(file.blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.filename ?? 'recepisse.pdf'
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  const fiche = useMutation({
    mutationFn: () => session.api.download(`/admin/applications/${id}/fiche`),
    onSuccess: (file) => saveFile(file, 'fiche-recapitulative.pdf'),
    onError: (error) => toast.error(errorMessage(error)),
  })

  if (detail.isError) return <Alert tone="danger">{errorMessage(detail.error)}</Alert>
  const d = detail.data
  if (!d) return <div className="skeleton" style={{ height: 400 }} aria-busy="true" />
  const sections = shown && shown !== latest ? (snapshot.data?.content.summary ?? []) : d.summary

  return (
    <>
      <div>
        <Link to="/candidatures" className="btn btn-ghost btn-sm">
          <ArrowLeft aria-hidden="true" /> Candidatures
        </Link>
      </div>
      <PageHeader
        eyebrow={`${d.competition.reference} · ${d.competition.session}`}
        title={`${d.candidate.lastName?.toUpperCase() ?? ''} ${d.candidate.firstName ?? ''}`.trim()}
        actions={
          <div className="row" style={{ gap: 'var(--space-2)' }}>
            {d.receiptAvailable && (
              <Button
                variant="secondary"
                icon={<FileDown />}
                loading={download.isPending}
                onClick={() => download.mutate()}
              >
                Récépissé
              </Button>
            )}
            {latest !== null && (
              <Button
                variant="secondary"
                icon={<FileDown />}
                loading={fiche.isPending}
                onClick={() => fiche.mutate()}
              >
                Fiche récapitulative
              </Button>
            )}
            {['pending', 'failed', 'cancelled', 'processing'].includes(d.paymentStatus) && (
              <Can perm="payment.manual_entry">
                <Button variant="secondary" icon={<Banknote />} onClick={() => setPaying(true)}>
                  Saisir un paiement
                </Button>
              </Can>
            )}
            {d.can.requestCorrection && (
              <Button variant="primary" icon={<FilePen />} onClick={() => setCorrecting(true)}>
                Demander une correction
              </Button>
            )}
          </div>
        }
      />
      <div className="stack" style={{ gap: 'var(--space-5)' }}>
        <Card>
          <CardBody>
            <InfoGrid>
              <InfoItem label="Matricule de candidature">
                <MatriculeTag kind="candidature" value={d.applicationNumber} />
              </InfoItem>
              <InfoItem label="Matricule utilisateur">
                {d.candidate.userNumber ? (
                  <MatriculeTag kind="utilisateur" value={d.candidate.userNumber} />
                ) : (
                  <span className="text-muted">Non visible avec votre profil</span>
                )}
              </InfoItem>
              <InfoItem label="Dossier">
                <StatusBadge domain="application" status={d.status} />
              </InfoItem>
              <InfoItem label="Paiement">
                <StatusBadge domain="payment" status={d.paymentStatus} />
              </InfoItem>
              <InfoItem label="Déposé le">{when(d.submittedAt)}</InfoItem>
              <InfoItem label="Référence de dépôt">
                <span className="mono">{d.submissionRef ?? '—'}</span>
              </InfoItem>
              <InfoItem label="Courriel">{d.candidate.email}</InfoItem>
              <InfoItem label="Formulaire">Version {d.form.version}</InfoItem>
            </InfoGrid>
          </CardBody>
        </Card>

        {d.submittedAt && <InstructionCard d={d} />}

        <Tabs
          label="Rubriques du dossier"
          value={tab}
          onChange={(v) => setTab(v as Tab)}
          items={[
            { value: 'infos', label: 'Informations' },
            { value: 'reponses', label: 'Réponses' },
            { value: 'pieces', label: 'Pièces' },
            { value: 'paiement', label: 'Paiement' },
            { value: 'historique', label: 'Historique' },
            {
              value: 'decisions',
              label: `Décisions${d.decisions.length ? ` · ${d.decisions.length}` : ''}`,
            },
          ]}
        />

        {tab === 'infos' && (
          <div className="grid-2">
            <SystemChecksCard checks={d.systemChecks} />
            <Card>
              <CardHeader title="Candidat" />
              <CardBody>
                <InfoGrid>
                  <InfoItem label="Nom">
                    {d.candidate.lastName?.toUpperCase()} {d.candidate.firstName}
                  </InfoItem>
                  <InfoItem label="Courriel">{d.candidate.email}</InfoItem>
                  <InfoItem label="Téléphone">{show(d.candidate.phone)}</InfoItem>
                  <InfoItem label="Pièce d’identité">
                    {d.candidate.idDocumentType
                      ? ID_DOCUMENT_TYPES[d.candidate.idDocumentType].label
                      : '—'}
                  </InfoItem>
                  <InfoItem label="Prise en charge">{when(d.reviewStartedAt)}</InfoItem>
                  <InfoItem label="Décision validée">{when(d.decidedAt)}</InfoItem>
                </InfoGrid>
              </CardBody>
            </Card>
          </div>
        )}

        {tab === 'reponses' && (
          <>
            <Card>
              <CardHeader
                title="Contenu du dossier"
                actions={
                  d.snapshots.length > 1 ? (
                    <SegmentedPills
                      label="Version"
                      value={String(shown)}
                      onChange={(v) => setVersion(Number(v))}
                      options={d.snapshots.map((s) => ({
                        value: String(s.version),
                        label:
                          s.kind === 'submission'
                            ? `v${s.version} · dépôt`
                            : `v${s.version} · corrigée`,
                      }))}
                    />
                  ) : undefined
                }
              />
              <CardBody>
                {shown && shown !== latest && (
                  <Alert tone="info">
                    Version initiale telle que déposée (consultation seule). La version en vigueur
                    est la v{latest}.
                  </Alert>
                )}
                {!d.submittedAt && (
                  <Alert tone="info">
                    Brouillon : ce contenu n’a pas été déposé et peut encore changer.
                  </Alert>
                )}
                <div
                  className="stack"
                  style={{ gap: 'var(--space-5)', marginBlockStart: 'var(--space-3)' }}
                >
                  {sections.map((section) => (
                    <section key={section.key}>
                      <h3 className="summary-title">{section.title}</h3>
                      <dl className="summary-list">
                        {section.items.map((item) => (
                          <div key={item.key}>
                            <dt>{item.label}</dt>
                            <dd>{show(item.value)}</dd>
                          </div>
                        ))}
                      </dl>
                    </section>
                  ))}
                </div>
              </CardBody>
            </Card>
            <Card>
              <CardHeader title="Corrections après dépôt" />
              <CardBody>
                {d.corrections.length ? (
                  <CorrectionList corrections={d.corrections} />
                ) : (
                  <p className="text-muted" style={{ margin: 0 }}>
                    Aucune correction. Toute correction est motivée et validée par une seconde
                    personne ; la version initiale reste consultable.
                  </p>
                )}
              </CardBody>
            </Card>
          </>
        )}

        {tab === 'pieces' && (
          <>
            {d.submittedAt && (
              <IdentityScansCard
                applicationId={d.id}
                documentType={d.candidate.idDocumentType}
                scans={d.identityScans}
                canReview={d.can.reviewDocuments}
              />
            )}
            <DocumentsCard
              applicationId={d.id}
              schema={d.form.schema}
              documents={d.documents}
              canReview={d.can.reviewDocuments}
            />
          </>
        )}

        {tab === 'paiement' && (
          <PaymentHistory applicationNumber={d.applicationNumber} status={d.paymentStatus} />
        )}

        {tab === 'historique' && (
          <div className="grid-2">
            <Card>
              <CardHeader title="Historique du dossier" />
              <CardBody>
                <ol className="timeline">
                  {d.history.map((h, i) => (
                    <li key={`${h.to}-${i}`}>
                      <strong>{h.toLabel}</strong>
                      <div className="timeline-meta">
                        {when(h.at)}
                        {h.reason ? ` — ${h.reason}` : ''}
                      </div>
                    </li>
                  ))}
                </ol>
              </CardBody>
            </Card>
            <ComplementsCard complements={d.complements} />
          </div>
        )}

        {tab === 'decisions' && <DecisionsCard decisions={d.decisions} />}
      </div>
      {paying && <ManualPaymentModal detail={d} onClose={() => setPaying(false)} />}
      {correcting && (
        <Can perm="candidature.correct_after_submit">
          <CorrectionModal detail={d} onClose={() => setCorrecting(false)} />
        </Can>
      )}
    </>
  )
}

export function CorrectionList({ corrections }: { corrections: Correction[] }) {
  return (
    <ul className="diff-list">
      {corrections.map((c) => (
        <li key={c.id}>
          <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <Badge variant={CORRECTION_VARIANT[c.status]}>{CORRECTION_STATUSES[c.status]}</Badge>
            <span className="text-muted" style={{ fontSize: 'var(--text-sm)' }}>
              {c.requestedBy.label}, {when(c.requestedAt)}
            </span>
          </div>
          <ul style={{ margin: 'var(--space-2) 0', paddingInlineStart: '1.2em' }}>
            {c.changes.map((ch) => (
              <li key={ch.key}>
                <strong>{ch.label}</strong> : <del>{show(ch.beforeDisplay)}</del> →{' '}
                <ins>{show(ch.afterDisplay)}</ins>
              </li>
            ))}
          </ul>
          <div style={{ fontSize: 'var(--text-sm)' }}>Motif : {c.reason}</div>
          {c.decidedBy && (
            <div className="text-muted" style={{ fontSize: 'var(--text-sm)' }}>
              {CORRECTION_STATUSES[c.status]} par {c.decidedBy.label}, {when(c.decidedAt)} —{' '}
              {c.decisionNote}
            </div>
          )}
        </li>
      ))}
    </ul>
  )
}

/** Éditeur de valeur selon le type du champ (pas de saisie libre pour une liste). */
function ValueEditor({
  field,
  value,
  onChange,
  error,
}: {
  field: FormField
  value: unknown
  onChange: (v: unknown) => void
  error?: string
}) {
  const label = 'Nouvelle valeur'
  if (field.type === 'select' || field.type === 'radio')
    return (
      <Select
        label={label}
        value={typeof value === 'string' ? value : ''}
        error={error}
        placeholder="— Choisir —"
        options={(field.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
        onChange={(e) => onChange(e.target.value || null)}
      />
    )
  if (field.type === 'checkbox')
    return (
      <Select
        label={label}
        value={value === true ? 'oui' : value === false ? 'non' : ''}
        error={error}
        placeholder="— Choisir —"
        options={[
          { value: 'oui', label: 'Oui' },
          { value: 'non', label: 'Non' },
        ]}
        onChange={(e) => onChange(e.target.value === 'oui')}
      />
    )
  if (field.type === 'multiselect')
    return (
      <fieldset className="field">
        <legend className="field-label">{label}</legend>
        {(field.options ?? []).map((o) => {
          const list = Array.isArray(value) ? (value as string[]) : []
          return (
            <label key={o.value} className="check-row">
              <input
                type="checkbox"
                checked={list.includes(o.value)}
                onChange={(e) =>
                  onChange(
                    e.target.checked ? [...list, o.value] : list.filter((v) => v !== o.value)
                  )
                }
              />
              <span>{o.label}</span>
            </label>
          )
        })}
        {error && <p className="field-error">{error}</p>}
      </fieldset>
    )
  const multiline = field.type === 'textarea'
  return multiline ? (
    <Textarea
      label={label}
      rows={3}
      value={typeof value === 'string' ? value : ''}
      error={error}
      onChange={(e) => onChange(e.target.value)}
    />
  ) : (
    <Input
      label={label}
      type={
        field.type === 'date'
          ? 'date'
          : field.type === 'integer' || field.type === 'decimal'
            ? 'number'
            : 'text'
      }
      value={value === null || value === undefined ? '' : String(value)}
      error={error}
      onChange={(e) =>
        onChange(
          field.type === 'integer' || field.type === 'decimal'
            ? e.target.value === ''
              ? null
              : Number(e.target.value)
            : e.target.value
        )
      }
    />
  )
}

function CorrectionModal({ detail, onClose }: { detail: Detail; onClose: () => void }) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const editable = allFields(detail.form.schema).filter(
    (f) => !SYSTEM_TYPES.includes(f.type) && !FILE_TYPES.includes(f.type)
  )
  const [rows, setRows] = useState<{ key: string; value: unknown }[]>([{ key: '', value: null }])
  const [reason, setReason] = useState('')
  const request = useMutation({
    mutationFn: () =>
      session.api.post(`/admin/applications/${detail.id}/corrections`, {
        changes: Object.fromEntries(rows.filter((r) => r.key).map((r) => [r.key, r.value])),
        reason,
      }),
    onSuccess: async () => {
      toast.success('Demande de correction transmise pour validation')
      await queryClient.invalidateQueries({ queryKey: [...APPLICATIONS_KEY, detail.id] })
      await queryClient.invalidateQueries({ queryKey: ['admin', 'corrections'] })
      onClose()
    },
  })
  const errors = fieldErrors(request.error)
  const ready = rows.some((r) => r.key) && reason.trim().length >= 10

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="Demander une correction après dépôt"
      description="La correction ne s’applique qu’après validation par une autre personne habilitée. La version déposée reste consultable."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={request.isPending}
            disabled={!ready}
            onClick={() => request.mutate()}
          >
            Transmettre pour validation
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        {request.isError && (
          <Alert tone="danger">
            {Object.entries(errors).length
              ? Object.values(errors).join(' ')
              : errorMessage(request.error)}
          </Alert>
        )}
        {rows.map((row, i) => {
          const field = editable.find((f) => f.key === row.key)
          return (
            <div key={i} className="card" style={{ padding: 'var(--space-4)' }}>
              <div className="stack" style={{ gap: 'var(--space-3)' }}>
                <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'end' }}>
                  <div className="grow">
                    <Select
                      label="Champ à corriger"
                      value={row.key}
                      placeholder="— Choisir un champ —"
                      options={editable
                        .filter((f) => f.key === row.key || !rows.some((r) => r.key === f.key))
                        .map((f) => ({ value: f.key, label: f.label }))}
                      onChange={(e) =>
                        setRows(
                          rows.map((r, j) => (j === i ? { key: e.target.value, value: null } : r))
                        )
                      }
                    />
                  </div>
                  {rows.length > 1 && (
                    <Button
                      variant="ghost"
                      icon={<Trash2 />}
                      aria-label="Retirer ce champ"
                      onClick={() => setRows(rows.filter((_, j) => j !== i))}
                    />
                  )}
                </div>
                {field && (
                  <>
                    <p className="text-muted" style={{ margin: 0, fontSize: 'var(--text-sm)' }}>
                      Valeur déposée :{' '}
                      <strong>
                        {detail.summary.flatMap((s) => s.items).find((it) => it.key === field.key)
                          ?.value ?? '—'}
                      </strong>
                    </p>
                    <ValueEditor
                      field={field}
                      value={row.value}
                      error={errors[`changes.${field.key}`]}
                      onChange={(value) =>
                        setRows(rows.map((r, j) => (j === i ? { ...r, value } : r)))
                      }
                    />
                  </>
                )}
              </div>
            </div>
          )
        })}
        {rows.length < editable.length && (
          <div>
            <Button
              variant="ghost"
              size="sm"
              icon={<Plus />}
              onClick={() => setRows([...rows, { key: '', value: null }])}
            >
              Corriger un autre champ
            </Button>
          </div>
        )}
        <Textarea
          label="Motif de la correction"
          required
          rows={3}
          hint="10 caractères minimum. Ex. : erreur matérielle constatée sur la pièce justificative."
          value={reason}
          error={errors.reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
    </Modal>
  )
}

/** Saisie manuelle exceptionnelle (ADR-001) : paiement reçu hors plateforme. */
function ManualPaymentModal({ detail, onClose }: { detail: Detail; onClose: () => void }) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [externalReference, setExternalReference] = useState('')
  const [paidAt, setPaidAt] = useState('')
  const save = useMutation({
    mutationFn: () =>
      session.api.post(`/admin/applications/${detail.id}/payments/manual`, {
        reason,
        externalReference,
        paidAt: paidAt || undefined,
      }),
    onSuccess: async () => {
      toast.success('Paiement enregistré')
      await queryClient.invalidateQueries({ queryKey: APPLICATIONS_KEY })
      onClose()
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const errors = fieldErrors(save.error)
  return (
    <Modal
      open
      onClose={onClose}
      title="Saisir un paiement reçu hors plateforme"
      description={detail.applicationNumber}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={reason.trim().length < 10 || externalReference.trim().length < 3}
            onClick={() => save.mutate()}
          >
            Enregistrer le paiement
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        <Alert tone="warning">
          Procédure exceptionnelle, tracée au journal d’audit. Une tentative de paiement en ligne
          encore ouverte sera close.
        </Alert>
        <Input
          label="Référence de la pièce comptable"
          required
          mono
          hint="Quittance, bordereau de virement…"
          value={externalReference}
          error={errors.externalReference}
          onChange={(e) => setExternalReference(e.target.value)}
        />
        <Input
          label="Date du paiement"
          type="date"
          value={paidAt}
          error={errors.paidAt}
          onChange={(e) => setPaidAt(e.target.value)}
        />
        <Textarea
          label="Motif"
          required
          rows={3}
          hint="10 caractères minimum."
          value={reason}
          error={errors.reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
    </Modal>
  )
}
